/**
 * User activation status for workflow management
 */
export enum UserActivationStatus {
  /** User account is pending activation (email not verified) */
  PENDING = "pending",
  /** User account is fully active and operational */
  ACTIVE = "active",
  /** User account is temporarily suspended (can be reactivated) */
  SUSPENDED = "suspended",
  /** User account is permanently deactivated */
  DEACTIVATED = "deactivated",
}

export interface User {
  id: string;
  tenantId: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  permissions: string[];
  tier: TenantTier;
  active: boolean;
  /** @deprecated Authorization is role-derived. Retained only for legacy view-model compatibility. */
  isStaff: boolean;
  /** @deprecated Authorization is role-derived. Use the exact `super_admin` role. */
  isSuperuser: boolean;
  lastLoginAt?: Date;
  createdAt: Date;
  updatedAt: Date;

  // Activation workflow fields
  emailVerified?: boolean;
  emailVerifiedAt?: Date;
  lockedAt?: Date;
  lockReason?: string;
  activationStatus?: UserActivationStatus;
}

/**
 * Compute activation status from user fields
 */
export function computeActivationStatus(user: User): UserActivationStatus {
  if (user.lockedAt) {
    return UserActivationStatus.SUSPENDED;
  }
  if (!user.active) {
    return UserActivationStatus.DEACTIVATED;
  }
  if (!user.emailVerified) {
    return UserActivationStatus.PENDING;
  }
  return UserActivationStatus.ACTIVE;
}

/**
 * Request to change user activation status
 */
export interface ActivationStatusChangeRequest {
  userId: string;
  newStatus: UserActivationStatus;
  reason?: string;
}

/**
 * Password reset request
 */
export interface PasswordResetRequest {
  email: string;
}

/**
 * Password reset confirmation
 */
export interface PasswordResetConfirmation {
  token: string;
  newPassword: string;
}

export interface AuthUser extends User {
  token: string;
  refreshToken: string;
  tokenExpiry: Date;
}

export enum TenantTier {
  FREE = "Free",
  PREMIUM = "Premium",
  ENTERPRISE = "Enterprise",
  INTERNAL = "Internal",
}

export interface LoginRequest {
  username: string;
  password: string;
  tenantId?: string;
}

export interface LoginResponse {
  user: User;
  token: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  tenantId: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * GAP-CP-005-B — FE mirror of the backend wildcard permission matcher
 * `razor_security::rbac::permissions::PermissionCheck::allows_required`
 * (crates/razor_security/src/rbac/permissions.rs). One held permission is
 * checked against one COMPLETE required permission name without truncating
 * multi-segment resources such as `admin:users:view`.
 *
 * Wildcards are intentionally limited to the exact maintained forms the
 * backend accepts:
 *   - `*:*`                — global grant
 *   - trailing `prefix:*`  — e.g. `copilot:*` allows `copilot:chat:read`
 *   - leading `*:action`   — e.g. `*:read` allows `copilot:chat:read`
 * Mid-string wildcards and colonless wildcard tokens fail closed, mirroring
 * the server. Pure and DI-free so lightweight render gates can share the
 * decision without pulling in any service graph. UX gate only — the backend
 * enforces the same rule independently.
 */
export function permissionAllowsRequired(permission: string, required: string): boolean {
  if (permission === required) {
    return true;
  }

  if (!required.includes(':')) {
    return false;
  }

  if (permission === '*:*') {
    return true;
  }

  if (permission.endsWith(':*')) {
    const prefix = permission.slice(0, -2);
    return prefix.length > 0 && prefix !== '*' && required.startsWith(`${prefix}:`);
  }

  if (permission.startsWith('*:')) {
    const action = permission.slice(2);
    const lastColon = required.lastIndexOf(':');
    return (
      action.length > 0 &&
      action !== '*' &&
      lastColon >= 0 &&
      required.slice(lastColon + 1) === action
    );
  }

  return false;
}

/**
 * GAP-CP-005-B — FE mirror of the backend
 * `PermissionCheck::check_required`: does ANY held permission (exact or
 * wildcard, per {@link permissionAllowsRequired}) satisfy the complete
 * required permission name? Role names deliberately play no part in this
 * decision — the backend gates are permission-only.
 */
export function permissionsAllow(permissions: readonly string[], required: string): boolean {
  return permissions.some((permission) => permissionAllowsRequired(permission, required));
}
